import {
  Outlet, NavLink, useNavigate
} from 'react-router';
import { useAuth } from '../hooks/useAuth';
import {
  LogOut,
  Menu,
  ChevronDown,
  ScrollText,
  Shield,
  X,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';
import { ModuleNavigation } from './ModuleNavigation';
import { formatChangelog } from '../utils/changelog';
import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { Avatar } from './Avatar';
import { useInstance } from '../contexts/InstanceContext';
import { HeaderSearch } from '../search/HeaderSearch';

// --- Helper Components ---

export default function Layout() {
  const { user, logout, hasMenuAccess } = useAuth();
  const instance = useInstance();
  const navigate = useNavigate();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Collapsed state for desktop
  // Default to false, could persist in localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', isCollapsed.toString());
  }, [isCollapsed]);

  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const accountDropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    setAccountDropdownOpen(false);
    await logout();
    navigate('/login');
  };

  // Close account dropdown and flyouts when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Account Dropdown
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target as Node)) {
        setAccountDropdownOpen(false);
      }

      // Close flyouts when collapsed and clicking outside
      // We check if click is outside of sidebar nav
      // BUT we also need to allow clicking INSIDE the flyout which is rendered inside the sidebar DOM
      // The SidebarGroup renders children inside.
    };

    // Add specific listener for closing flyouts on outside click (iPad/Touch friendly)
    const handleGlobalClick = (e: MouseEvent) => {
      if (isCollapsed && openMenu) {
        const target = e.target as HTMLElement;
        // If the click is NOT inside a menu group/flyout
        // The flyout is a child of .group/menu, so `closest` will work if flyout is open.
        if (!target.closest('.group\\/menu')) {
          setOpenMenu(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('click', handleGlobalClick); // check click for flyouts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [isCollapsed, openMenu]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar overlay (unchanged) */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 transition-all duration-300 flex flex-col',
          // Mobile state
          mobileSidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0',
          // Desktop Collapsed state
          isCollapsed ? 'lg:w-20' : 'lg:w-64'
        )}
      >
        {/* Logo Section */}
        <div className="flex items-center gap-3 px-3 h-16 border-b border-gray-200 shrink-0 overflow-hidden">
          <img src="/api/instance/assets/signet" alt={instance.name.short} className="h-6 w-auto shrink-0 transition-none" />
          {!isCollapsed && (
            <div className="min-w-0 transition-opacity duration-300">
              <h1 className="font-semibold text-charcoal truncate">{instance.name.short}</h1>
              <p className="text-xs text-gray-500 truncate">{instance.branding.subtitle}</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav
          className={clsx(
            "flex-1 p-3 space-y-1 no-scrollbar min-h-0",
            isCollapsed ? "overflow-visible" : "overflow-y-auto" // CRITICAL FIX: Overflow handling
          )}
        >
          <ModuleNavigation
            isCollapsed={isCollapsed}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            onNavigate={() => setMobileSidebarOpen(false)}
          />
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-200 shrink-0 flex flex-col gap-2">
          <div className={clsx("flex items-center", isCollapsed ? "justify-center" : "justify-between")}>
            {!isCollapsed && (
              <button
                onClick={() => setChangelogOpen(true)}
                className="text-xs text-gray-400 hover:text-primary transition-colors"
              >
                v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'}
              </button>
            )}

            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors hidden lg:flex"
              title={isCollapsed ? "Erweitern" : "Einklappen"}
            >
              {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
          </div>

          {isCollapsed && (
            <button
              onClick={() => setChangelogOpen(true)}
              className="text-[10px] text-gray-400 hover:text-primary transition-colors w-full text-center"
            >
              v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'}
            </button>
          )}
        </div>

      </aside>

      {/* Changelog Modal */}
      {
        changelogOpen && (
          <div className="fixed inset-0 z-100 flex justify-end pointer-events-none mt-0!">
            <div className="absolute inset-0 bg-black/20 backdrop-blur-xs pointer-events-auto transition-opacity duration-300 ease-in-out" onClick={() => setChangelogOpen(false)} />
            <div className="relative w-full max-w-md bg-white h-full shadow-2xl overflow-hidden pointer-events-auto animate-in slide-in-from-right duration-300 ease-out flex flex-col border-l border-gray-100">
              <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-white/80 backdrop-blur-xl sticky top-0 z-20 shrink-0">
                <h2 className="text-xl font-bold text-gray-900 tracking-tight">Changelog</h2>
                <button
                  onClick={() => setChangelogOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-charcoal transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                {typeof __APP_CHANGELOG__ !== 'undefined' && __APP_CHANGELOG__
                  ? formatChangelog(__APP_CHANGELOG__)
                  : <div className="text-center py-12 text-gray-400 font-medium">Kein Changelog verfügbar</div>
                }
              </div>
            </div>
          </div>
        )
      }

      {/* Main content */}
      <div className={clsx(
        "flex-1 flex flex-col min-h-screen transition-all duration-300",
        isCollapsed ? "lg:pl-20" : "lg:pl-64"
      )}>
        {/* Header - Mobile */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="p-2 hover:bg-gray-50 rounded-lg"
          >
            <Menu size={24} className="text-charcoal" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/api/instance/assets/signet" alt={instance.name.short} className="h-5 w-auto" />
            <span className="font-semibold text-charcoal">{instance.branding.subtitle}</span>
          </div>
          <div className="flex items-center">
            <NavLink to="/suche" className="p-2 hover:bg-gray-50 rounded-lg text-gray-500" aria-label="Suche">
              <Search size={20} />
            </NavLink>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-gray-50 rounded-lg text-gray-500"
              title="Abmelden"
            >
              <LogOut size={20} />
            </button>
          </div>
        </header>

        {/* Header - Desktop (same height as sidebar logo) */}
        <header className="hidden lg:flex items-center justify-between gap-6 px-8 h-16 bg-white border-b border-gray-200">
          <HeaderSearch />
          {/* Account Dropdown */}
          <div className="relative" ref={accountDropdownRef}>
            <button
              onClick={() => setAccountDropdownOpen(!accountDropdownOpen)}
              className={clsx(
                'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                accountDropdownOpen
                  ? 'bg-gray-100'
                  : 'hover:bg-gray-50'
              )}
            >
              {/* Avatar */}
              <Avatar
                src={user?.picture}
                name={user?.name}
                email={user?.email}
                size="md"
              />
              {/* Name */}
              <span className="text-sm font-medium text-charcoal">
                {user?.name || user?.email}
              </span>
              <ChevronDown
                size={16}
                className={clsx(
                  'text-gray-400 transition-transform',
                  accountDropdownOpen && 'rotate-180'
                )}
              />
            </button>

            {/* Dropdown Menu */}
            {accountDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                {/* User Info */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-charcoal">
                    {user?.name || user?.email}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.email}
                  </p>
                </div>

                {/* Admin Section */}
                {hasMenuAccess('admin') && (
                  <>
                    <div className="py-2">

                      {hasMenuAccess('admin.logs') && (
                        <NavLink
                          to="/admin/logs"
                          onClick={() => setAccountDropdownOpen(false)}
                          className={({ isActive }) =>
                            clsx(
                              'flex items-center gap-3 px-4 py-2 text-sm transition-colors',
                              isActive
                                ? 'bg-primary/10 text-primary'
                                : 'text-gray-700 hover:bg-gray-50'
                            )
                          }
                        >
                          <ScrollText size={16} />
                          <span>API-Logs</span>
                        </NavLink>
                      )}
                      {hasMenuAccess('admin.menu-permissions') && (
                        <NavLink
                          to="/admin/menu-permissions"
                          onClick={() => setAccountDropdownOpen(false)}
                          className={({ isActive }) =>
                            clsx(
                              'flex items-center gap-3 px-4 py-2 text-sm transition-colors',
                              isActive
                                ? 'bg-primary/10 text-primary'
                                : 'text-gray-700 hover:bg-gray-50'
                            )
                          }
                        >
                          <Shield size={16} />
                          <span>Menü-Berechtigungen</span>
                        </NavLink>
                      )}
                    </div>
                    <div className="border-t border-gray-100" />
                  </>
                )}

                {/* Logout */}
                <div className="py-2">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <LogOut size={16} />
                    <span>Abmelden</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div >
  );
}
